import {
  Add,
  ArrowDownward,
  ArrowUpward,
  DeleteOutlined,
  Remove,
} from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  Grid2,
  IconButton,
  Popover,
  Typography,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { ControlledSelectField } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/controlled-fields/controlled-select-field";
import { FC, memo, useMemo, useState } from "react";
import { HexColorPicker } from "react-colorful";
import { v4 as uuidv4 } from "uuid";
import {
  AnswerType,
  APP_SOURCES,
  EntityRef,
  ExtendedFieldError,
  FormQuestion,
  FormQuestionOption,
  getEntityRefTarget,
} from "vmd-types";

import { CATEGORY_OPTION_DEFAULT_COLOR } from "../../../constants/colors";
import { AntSwitch } from "../../components/ant-switch";
import { DraftTextField } from "./draft-text-field";
import {
  boundFieldNote,
  getAnswerTypeOptions,
  isSelectAnswerType,
  t,
} from "./labels";

const MAX_OPTIONS = 20;

export type ConditionCandidate = {
  id: string;
  label: string;
  answerType: AnswerType;
  options?: FormQuestionOption[];
  entityRef?: EntityRef;
};

export interface FormQuestionEditorProps {
  question: FormQuestion;
  formIndex: number;
  blockIndex: number;
  questionIndex: number;
  isFirst: boolean;
  isLast: boolean;
  /**
   * Every question in the blueprint, including this one, which is filtered out
   * here. Passing the shared list keeps the prop identity stable across editors.
   */
  allQuestions: ConditionCandidate[];
  validationErrors: Record<string, ExtendedFieldError>;
  language: string;
  // Index-based so the parent can hand every editor the same callbacks.
  onChange: (index: number, question: FormQuestion) => void;
  onDelete: (index: number) => void;
  onMove: (index: number, direction: -1 | 1) => void;
}

const FormQuestionEditorComponent: FC<FormQuestionEditorProps> = ({
  question,
  formIndex,
  blockIndex,
  questionIndex,
  isFirst,
  isLast,
  allQuestions,
  validationErrors,
  language,
  onChange,
  onDelete,
  onMove,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openColorIndex, setOpenColorIndex] = useState<number | null>(null);

  const keyBase = `trackingItemForms.${formIndex}.blocks.${blockIndex}.questions.${questionIndex}`;
  const update = (patch: Partial<FormQuestion>) =>
    onChange(questionIndex, { ...question, ...patch });

  // Set from the workbook's entity_ref column; the widget and options then come
  // from the registry, so both are locked here.
  const boundRef = question.entityRef;

  // Built once per question rather than on every parent render; a 140-question
  // blueprint otherwise rebuilds this list for every editor on each keystroke.
  const conditionOptions = useMemo(
    () => [
      { value: "", label: t(language, "formBlocks.noCondition", "None") },
      ...allQuestions
        .filter((c) => c.id !== question.id)
        .map((c) => ({ value: c.id, label: c.label })),
    ],
    [allQuestions, question.id, language]
  );

  const controllingQuestion = allQuestions.find(
    (c) => c.id === question.conditionalOn?.questionId
  );
  const controllingIsSelect = controllingQuestion
    ? isSelectAnswerType(controllingQuestion.answerType)
    : false;
  const controllingEnumValues: readonly string[] =
    controllingQuestion?.entityRef
      ? (getEntityRefTarget(controllingQuestion.entityRef as EntityRef)
          .values ?? [])
      : [];
  const controllingHasOptions =
    controllingIsSelect || controllingEnumValues.length > 0;
  const equalsValues = question.conditionalOn?.equals ?? [];

  const setControllingQuestion = (questionId: string) =>
    update({ conditionalOn: questionId ? { questionId } : undefined });

  const setEquals = (values: string[]) => {
    if (!question.conditionalOn) return;
    update({
      conditionalOn: {
        questionId: question.conditionalOn.questionId,
        equals: values,
      },
    });
  };

  const updateOptions = (options: FormQuestionOption[]) =>
    onChange(questionIndex, { ...question, options });

  const addOption = () => {
    if ((question.options ?? []).length >= MAX_OPTIONS) return;
    updateOptions([
      ...(question.options ?? []),
      { id: uuidv4(), label: "", color: CATEGORY_OPTION_DEFAULT_COLOR },
    ]);
  };
  const changeOptionLabel = (index: number, label: string) => {
    const next = [...(question.options ?? [])];
    next[index] = { ...next[index], label };
    updateOptions(next);
  };
  const changeOptionColor = (index: number, color: string) => {
    const next = [...(question.options ?? [])];
    next[index] = { ...next[index], color };
    updateOptions(next);
  };
  const removeOption = (index: number) => {
    const next = [...(question.options ?? [])];
    next.splice(index, 1);
    updateOptions(next);
  };

  return (
    <Accordion
      elevation={0}
      disableGutters
      defaultExpanded={false}
      slotProps={{ transition: { mountOnEnter: true, unmountOnExit: true } }}
      sx={{
        border: "1px solid",
        borderColor: grey[300],
        borderRadius: "12px",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ "& .MuiAccordionSummary-content": { alignItems: "center" } }}
      >
        <Typography sx={{ fontWeight: 500, fontSize: 14, flexGrow: 1 }}>
          {question.label?.trim()
            ? question.label
            : question.fieldName?.trim()
              ? question.fieldName
              : t(language, "formBlocks.untitledQuestion", "Untitled question")}
        </Typography>
        <IconButton
          size="small"
          onClick={() => onMove(questionIndex, -1)}
          disabled={isFirst}
        >
          <ArrowUpward fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => onMove(questionIndex, 1)}
          disabled={isLast}
        >
          <ArrowDownward fontSize="small" />
        </IconButton>
        <FormControlLabel
          labelPlacement="start"
          sx={{ "& .MuiFormControlLabel-label": { fontSize: 14 } }}
          control={
            <AntSwitch
              sx={{ mr: 2, ml: 2 }}
              checked={question.enabled}
              onChange={(e) => update({ enabled: e.target.checked })}
            />
          }
          label={t(language, "activate", "Activate")}
        />
        <IconButton size="small" onClick={() => onDelete(questionIndex)}>
          <DeleteOutlined fontSize="small" />
        </IconButton>
      </AccordionSummary>
      <AccordionDetails
        sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
      >
        <Grid2 container spacing={2} alignItems="flex-start">
          <Grid2 size={6}>
            <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
              {t(language, "formBlocks.fieldName", "Field name")}
            </Typography>
            <DraftTextField
              value={question.fieldName ?? question.label ?? ""}
              onCommit={(val) => update({ fieldName: val })}
            />
            {boundRef && (
              <Typography sx={{ fontSize: 12, color: grey[600], mt: 0.5 }}>
                {boundFieldNote(language, boundRef)}
              </Typography>
            )}
          </Grid2>

          <Grid2 size={6}>
            <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
              {t(language, "formBlocks.fieldTextOnUI", "Field Text on UI")}
            </Typography>
            <DraftTextField
              value={question.label ?? question.fieldName ?? ""}
              onCommit={(val) => update({ label: val })}
              error={Boolean(validationErrors?.[`${keyBase}.label`])}
              helperText={validationErrors?.[`${keyBase}.label`]?.message}
            />
          </Grid2>

          <Grid2 size={6}>
            <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
              {t(language, "formBlocks.code", "Code")}
            </Typography>
            <DraftTextField
              value={question.code ?? ""}
              onCommit={(val) => update({ code: val })}
              error={Boolean(validationErrors?.[`${keyBase}.code`])}
              helperText={validationErrors?.[`${keyBase}.code`]?.message}
            />
          </Grid2>

          <Grid2 size={6}>
            <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
              {t(language, "formBlocks.answerTypeLabel", "Answer type")}
            </Typography>
            {boundRef ? (
              // A bound question renders from its column, so changing the answer
              // type here would have no effect. Show it read-only, with the target
              // it resolves to, since that is what decides the widget and options.
              <Box sx={{ py: 1 }}>
                <Typography sx={{ fontSize: 14, color: grey[700] }}>
                  {getAnswerTypeOptions(language).find(
                    (option) => option.value === question.answerType
                  )?.label ?? question.answerType}
                </Typography>
                <Typography sx={{ fontSize: 12, color: grey[600] }}>
                  {boundFieldNote(language, boundRef)}
                </Typography>
              </Box>
            ) : (
              <ControlledSelectField
                value={question.answerType}
                onChange={(val) => update({ answerType: val as AnswerType })}
                options={getAnswerTypeOptions(language)}
              />
            )}
          </Grid2>

          <Grid2 size={12}>
            <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
              {t(language, "formBlocks.description", "Field description")}
            </Typography>
            <DraftTextField
              value={question.description ?? ""}
              onCommit={(val) => update({ description: val })}
              multiline
            />
          </Grid2>

          <Grid2 size={12}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <FormControlLabel
                sx={{ "& .MuiFormControlLabel-label": { fontSize: 14 } }}
                control={
                  <Checkbox
                    checked={Boolean(question.mandatory)}
                    onChange={(e) => update({ mandatory: e.target.checked })}
                  />
                }
                label={t(language, "mandatory", "Mandatory")}
              />
              <FormControlLabel
                sx={{ "& .MuiFormControlLabel-label": { fontSize: 14 } }}
                control={
                  <Checkbox
                    checked={Boolean(question.readOnlyForOEs)}
                    onChange={(e) =>
                      update({ readOnlyForOEs: e.target.checked })
                    }
                  />
                }
                label={t(language, "formBlocks.readOnlyForOEs", "Disabled")}
              />
            </Box>
          </Grid2>

          <Grid2 size={12}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ width: 280, maxWidth: "100%" }}>
                <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 0.5 }}>
                  {t(language, "formBlocks.source", "Source")}
                </Typography>
                <ControlledSelectField
                  value={question.source ?? ""}
                  onChange={(val) =>
                    update({
                      source: val ? (val as FormQuestion["source"]) : undefined,
                    })
                  }
                  options={[
                    {
                      value: "",
                      label: t(language, "formBlocks.noCondition", "None"),
                    },
                    ...Object.values(APP_SOURCES).map((value) => ({
                      value,
                      label: value,
                    })),
                  ]}
                />
              </Box>
              <FormControlLabel
                sx={{ "& .MuiFormControlLabel-label": { fontSize: 14 } }}
                control={
                  <Checkbox
                    checked={Boolean(question.showSource)}
                    onChange={(e) => update({ showSource: e.target.checked })}
                  />
                }
                label={t(language, "formBlocks.showSource", "Show Source")}
              />
            </Box>
          </Grid2>
        </Grid2>

        {/* Options — only for unbound select answer types; a bound question takes
          its options from the entity_ref registry. */}
        {!boundRef && isSelectAnswerType(question.answerType) && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
              {t(language, "formBlocks.options", "Options")}
            </Typography>
            {validationErrors?.[`${keyBase}.options`] && (
              <Typography sx={{ fontSize: 12, color: "error.main" }}>
                {validationErrors[`${keyBase}.options`]?.message}
              </Typography>
            )}
            {(question.options ?? []).map((option, optionIndex) => (
              <Box
                key={option.id}
                sx={{ display: "flex", alignItems: "center", gap: 2 }}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <DraftTextField
                    value={option.label}
                    onCommit={(val) => changeOptionLabel(optionIndex, val)}
                    error={Boolean(
                      validationErrors?.[
                        `${keyBase}.options.${optionIndex}.label`
                      ]
                    )}
                    helperText={
                      validationErrors?.[
                        `${keyBase}.options.${optionIndex}.label`
                      ]?.message
                    }
                  />
                </Box>
                <Box
                  onClick={(e) => {
                    setOpenColorIndex(optionIndex);
                    setAnchorEl(e.currentTarget);
                  }}
                  sx={{
                    height: 30,
                    width: 30,
                    flexShrink: 0,
                    background: option.color ?? CATEGORY_OPTION_DEFAULT_COLOR,
                    "&:hover": {
                      background: `${option.color ?? CATEGORY_OPTION_DEFAULT_COLOR}50`,
                    },
                    borderRadius: "50%",
                    cursor: "pointer",
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => removeOption(optionIndex)}
                  sx={{ color: theme.palette.primary.dark }}
                >
                  <Remove fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Popover
              open={openColorIndex !== null}
              anchorEl={anchorEl}
              onClose={() => {
                setOpenColorIndex(null);
                setAnchorEl(null);
              }}
              sx={{ zIndex: 20000 }}
            >
              <HexColorPicker
                color={
                  openColorIndex !== null
                    ? question.options?.[openColorIndex]?.color
                    : undefined
                }
                onChange={(color) => {
                  if (openColorIndex !== null)
                    changeOptionColor(openColorIndex, color);
                }}
              />
            </Popover>
            {(question.options?.length ?? 0) < MAX_OPTIONS && (
              <Box>
                <IconButton
                  onClick={addOption}
                  sx={{ color: theme.palette.primary.dark }}
                >
                  <Add />
                </IconButton>
              </Box>
            )}
          </Box>
        )}

        {/* Conditional on */}
        <Grid2 container spacing={2} alignItems="flex-start">
          <Grid2 size={6}>
            <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
              {t(language, "formBlocks.conditionalOn", "Conditional on")}
            </Typography>
            <ControlledSelectField
              value={question.conditionalOn?.questionId ?? ""}
              onChange={(val) => setControllingQuestion(val as string)}
              options={conditionOptions}
            />
          </Grid2>
          {question.conditionalOn?.questionId && (
            <Grid2 size={6}>
              <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
                {t(language, "formBlocks.conditionEquals", "Equals")}
              </Typography>
              {controllingHasOptions ? (
                <ControlledSelectField
                  multiple
                  value={equalsValues}
                  onChange={(val) =>
                    setEquals(Array.isArray(val) ? val : val ? [val] : [])
                  }
                  options={
                    controllingEnumValues.length > 0
                      ? controllingEnumValues.map((v) => ({
                          value: v,
                          label: v,
                        }))
                      : (controllingQuestion?.options ?? []).map((o) => ({
                          value: o.id,
                          label: o.label?.trim() || o.id,
                        }))
                  }
                />
              ) : (
                <DraftTextField
                  value={equalsValues[0] ?? ""}
                  onCommit={(val) => setEquals(val ? [val] : [])}
                />
              )}
            </Grid2>
          )}

          <Grid2 size={6}>
            <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
              {t(language, "formBlocks.comment", "Comment")}
            </Typography>
            <DraftTextField
              value={question.comment ?? ""}
              onCommit={(val) => update({ comment: val })}
            />
          </Grid2>
        </Grid2>
      </AccordionDetails>
    </Accordion>
  );
};

// Editing one question in a 60-question block must not re-render the other 59.
export const FormQuestionEditor = memo(FormQuestionEditorComponent);
